// src/components/pages/LandingPage.js

import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import TopPlayersTeaser from '../features/TopPlayersTeaser';

// The authenticated dashboard (and its heavy children: create-tournament modal,
// leaflet maps) only render for a logged-in user viewing their dashboard — keep
// them out of the marketing landing bundle.
const Dashboard = lazy(() => import('../dashboard/Dashboard'));

// Feature card accent colors for differentiation
const featureAccents = [
  { border: '#667eea', bg: 'rgba(102, 126, 234, 0.08)' }, // Purple
  { border: '#38b2ac', bg: 'rgba(56, 178, 172, 0.08)' },  // Teal
  { border: '#ed8936', bg: 'rgba(237, 137, 54, 0.08)' },  // Orange
  { border: '#667eea', bg: 'rgba(102, 126, 234, 0.08)' }, // Purple
  { border: '#38b2ac', bg: 'rgba(56, 178, 172, 0.08)' },  // Teal
  { border: '#ed8936', bg: 'rgba(237, 137, 54, 0.08)' },  // Orange
];

export default function LandingPage({
  onNavigate,
  onShowAuth,
  showDashboard,
  onSelectTournament,
  openTournamentFinder,
  openCreateModal,
  onNavigateToAdmin
}) {
  const { isAuthenticated } = useAuth();
  const { windowWidth } = useResponsive();
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  // Trigger entrance animations on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Responsive breakpoints
  const isMobileView = windowWidth < 768;
  const isTabletView = windowWidth >= 768 && windowWidth < 1024;

  // If showDashboard is true and user is authenticated, show Dashboard component
  if (showDashboard && isAuthenticated) {
    return (
      <div style={{ paddingTop: '64px' }}>
        <Suspense fallback={<div style={{ padding: 48, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading dashboard…</div>}>
          <Dashboard
            onCloseDashboard={() => onNavigate('landing')}
            onSelectTournament={onSelectTournament}
            openTournamentFinder={openTournamentFinder}
            openCreateModal={openCreateModal}
            onNavigateToAdmin={onNavigateToAdmin}
          />
        </Suspense>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--bg-primary)',
      paddingTop: '64px'
    }}>
      {/* Hero Section */}
      <section style={{
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        background: 'linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-secondary) 100%)',
        color: 'var(--text-on-dark)',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background pattern overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(circle at 20% 80%, rgba(255,255,255,0.12) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255,255,255,0.12) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(255,255,255,0.06) 0%, transparent 30%),
            radial-gradient(circle at 60% 70%, rgba(255,255,255,0.08) 0%, transparent 40%)
          `,
          pointerEvents: 'none'
        }} />

        {/* Subtle noise texture */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.03,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          pointerEvents: 'none'
        }} />

        {/* Hero Content with animations */}
        <div style={{
          maxWidth: '900px',
          marginBottom: '60px',
          position: 'relative',
          zIndex: 1,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
        }}>
          <h1 style={{
            fontSize: 'clamp(36px, 6vw, 60px)',
            fontWeight: '800',
            margin: '0 0 24px 0',
            lineHeight: '1.1',
            letterSpacing: '-0.03em',
            fontFamily: 'var(--font-display)'
          }}>
            The Modern Way to Run Pool Tournaments
          </h1>
          <p style={{
            fontSize: 'clamp(16px, 3vw, 20px)',
            margin: '0 0 40px 0',
            opacity: 0.95,
            maxWidth: '700px',
            marginLeft: 'auto',
            marginRight: 'auto',
            lineHeight: '1.7',
            fontWeight: '400'
          }}>
            Professional tournament management for pool leagues, bars, and billiards enthusiasts.
            Create brackets, manage players, and share results instantly.
          </p>
        </div>

        {/* Action Cards with staggered animation */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobileView ? '1fr' : 'repeat(2, 1fr)',
          gap: '30px',
          maxWidth: '900px',
          width: '100%',
          position: 'relative',
          zIndex: 1
        }}>
          {/* Find Tournaments Card */}
          <div
            role="button"
            tabIndex={0}
            aria-label="Find tournaments near you"
            onMouseEnter={() => setHoveredCard('find')}
            onMouseLeave={() => setHoveredCard(null)}
            onClick={() => onNavigate('finder')}
            onKeyDown={(e) => e.key === 'Enter' && onNavigate('finder')}
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '20px',
              padding: '40px 32px',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: hoveredCard === 'find' ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
              boxShadow: hoveredCard === 'find'
                ? '0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 2px rgba(102, 126, 234, 0.2)'
                : '0 8px 24px rgba(0, 0, 0, 0.1)',
              color: 'var(--text-primary)',
              opacity: isVisible ? 1 : 0,
              transitionDelay: '0.2s'
            }}
          >
            <div style={{
              marginBottom: '20px',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '20px',
                background: 'rgba(102, 126, 234, 0.1)',
                transition: 'transform 0.3s ease'
              }}>
                <img
                  src="/FindTourn_Icon.png"
                  alt="Find Tournaments"
                  style={{
                    width: '56px',
                    height: '56px',
                    objectFit: 'contain',
                    transform: hoveredCard === 'find' ? 'scale(1.1)' : 'scale(1)',
                    transition: 'transform 0.3s ease'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
            <h2 style={{
              fontSize: '26px',
              fontWeight: '700',
              margin: '0 0 12px 0',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-display)'
            }}>
              Find Tournaments
            </h2>
            <p style={{
              fontSize: '15px',
              color: 'var(--text-secondary)',
              margin: '0 0 24px 0',
              lineHeight: '1.7'
            }}>
              Discover pool tournaments near you. Browse by location, day of week, and format.
              View venue details and get directions instantly.
            </p>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 28px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'var(--text-on-dark)',
              borderRadius: '12px',
              fontWeight: '600',
              fontSize: '15px',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
              transition: 'all 0.2s ease',
              fontFamily: 'var(--font-display)'
            }}>
              Browse Events
              <span style={{ fontSize: '18px', transition: 'transform 0.2s ease', transform: hoveredCard === 'find' ? 'translateX(4px)' : 'translateX(0)' }}>→</span>
            </div>
          </div>

          {/* Create Tournament Card */}
          <div
            role="button"
            tabIndex={0}
            aria-label="Create a new tournament"
            onMouseEnter={() => setHoveredCard('create')}
            onMouseLeave={() => setHoveredCard(null)}
            onClick={() => onNavigate('local')}
            onKeyDown={(e) => e.key === 'Enter' && onNavigate('local')}
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '20px',
              padding: '40px 32px',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: hoveredCard === 'create' ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
              boxShadow: hoveredCard === 'create'
                ? '0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 2px rgba(56, 161, 105, 0.2)'
                : '0 8px 24px rgba(0, 0, 0, 0.1)',
              color: 'var(--text-primary)',
              opacity: isVisible ? 1 : 0,
              transitionDelay: '0.3s'
            }}
          >
            <div style={{
              marginBottom: '20px',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '20px',
                background: 'rgba(56, 161, 105, 0.1)',
                transition: 'transform 0.3s ease'
              }}>
                <img
                  src="/CreateTourn_Icon.png"
                  alt="Create Tournament"
                  style={{
                    width: '56px',
                    height: '56px',
                    objectFit: 'contain',
                    transform: hoveredCard === 'create' ? 'scale(1.1)' : 'scale(1)',
                    transition: 'transform 0.3s ease'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
            <h2 style={{
              fontSize: '26px',
              fontWeight: '700',
              margin: '0 0 12px 0',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-display)'
            }}>
              Create Tournament
            </h2>
            <p style={{
              fontSize: '15px',
              color: 'var(--text-secondary)',
              margin: '0 0 24px 0',
              lineHeight: '1.7'
            }}>
              Start a tournament in minutes. Works offline - no account needed.
              Save to the cloud later to share with players and venues.
            </p>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 28px',
              backgroundColor: 'var(--success)',
              color: 'var(--text-on-dark)',
              borderRadius: '12px',
              fontWeight: '600',
              fontSize: '15px',
              boxShadow: '0 4px 12px rgba(56, 161, 105, 0.3)',
              transition: 'all 0.2s ease',
              fontFamily: 'var(--font-display)'
            }}>
              Start Free
              <span style={{ fontSize: '18px', transition: 'transform 0.2s ease', transform: hoveredCard === 'create' ? 'translateX(4px)' : 'translateX(0)' }}>→</span>
            </div>
          </div>
        </div>

        {/* Quick Sign Up Prompt */}
        {!isAuthenticated && (
          <div style={{
            marginTop: '48px',
            fontSize: '16px',
            opacity: isVisible ? 0.9 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
            transitionDelay: '0.4s',
            position: 'relative',
            zIndex: 1
          }}>
            Want to save and share tournaments?{' '}
            <span
              onClick={() => onShowAuth('signup')}
              style={{
                textDecoration: 'underline',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Create a free account
            </span>
          </div>
        )}
      </section>

      {/* Top ranked players teaser (hidden until there are ranked players) */}
      <TopPlayersTeaser onNavigate={onNavigate} />

      {/* Quick Features Section */}
      <section style={{
        padding: '80px 20px',
        backgroundColor: 'var(--bg-secondary)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: 'clamp(28px, 5vw, 40px)',
            fontWeight: '800',
            textAlign: 'center',
            margin: '0 0 16px 0',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-display)',
            letterSpacing: '-0.02em'
          }}>
            Everything You Need
          </h2>
          <p style={{
            fontSize: '18px',
            textAlign: 'center',
            color: 'var(--text-secondary)',
            margin: '0 0 60px 0',
            maxWidth: '600px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            Professional features for running tournaments of any size
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobileView
              ? '1fr'
              : isTabletView
                ? 'repeat(2, 1fr)'
                : 'repeat(3, 1fr)',
            gap: '24px'
          }}>
            {/* Feature Cards */}
            {[
              { icon: '/Trophy_Icon.png', title: 'Multiple Formats', desc: 'Single Elimination, Double Elimination, Modified Single Elimination, and Round Robin', isImage: true },
              { icon: '/Host_Icon.png', title: 'Custom Branding', desc: 'Add logos, banners, colors, and sponsors to make tournaments your own', isImage: true },
              { icon: '/Calendar_Icon.png', title: 'Recurring Events', desc: 'Schedule weekly, bi-weekly, or monthly tournaments that players can easily find', isImage: true },
              { icon: '/MapPin_Icon.png', title: 'Location Maps', desc: 'Interactive maps help players find your venue with distance calculations', isImage: true },
              { icon: '/TableAssign_Icon.png', title: 'Table Assignment', desc: 'Smart automatic table assignment with manual override options', isImage: true },
              { icon: '/ViewTourn_Icon.png', title: 'Live Updates', desc: 'Real-time bracket updates that players can follow on any device', isImage: true }
            ].map((feature, index) => (
              <div
                key={index}
                onMouseEnter={() => setHoveredFeature(index)}
                onMouseLeave={() => setHoveredFeature(null)}
                style={{
                  padding: '32px 28px',
                  backgroundColor: 'var(--bg-card)',
                  borderRadius: '16px',
                  textAlign: 'center',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'default',
                  border: `1px solid ${hoveredFeature === index ? featureAccents[index].border : 'var(--border-light)'}`,
                  transform: hoveredFeature === index ? 'translateY(-6px)' : 'translateY(0)',
                  boxShadow: hoveredFeature === index
                    ? `0 12px 24px rgba(0, 0, 0, 0.08), 0 0 0 1px ${featureAccents[index].border}20`
                    : '0 2px 8px rgba(0, 0, 0, 0.04)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Top accent bar */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${featureAccents[index].border}, ${featureAccents[(index + 1) % 6].border})`,
                  opacity: hoveredFeature === index ? 1 : 0,
                  transition: 'opacity 0.3s ease'
                }} />

                <div style={{
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    width: '72px',
                    height: '72px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '16px',
                    background: featureAccents[index].bg,
                    transition: 'transform 0.3s ease',
                    transform: hoveredFeature === index ? 'scale(1.1)' : 'scale(1)'
                  }}>
                    {feature.isImage ? (
                      <img
                        src={feature.icon}
                        alt={feature.title}
                        loading="lazy"
                        style={{
                          width: '48px',
                          height: '48px',
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: '40px' }}>{feature.icon}</span>
                    )}
                  </div>
                </div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  margin: '0 0 12px 0',
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-display)'
                }}>
                  {feature.title}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--text-secondary)',
                  margin: 0,
                  lineHeight: '1.7'
                }}>
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Split Feature Highlight */}
      <section style={{
        padding: '80px 20px',
        backgroundColor: 'var(--bg-tertiary)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isMobileView ? '1fr' : 'repeat(2, 1fr)',
          gap: '32px'
        }}>
          {/* For Players */}
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '20px',
            padding: '40px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
            border: '1px solid var(--border-light)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Accent gradient border */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '4px',
              height: '100%',
              background: 'linear-gradient(180deg, #38a169, #38b2ac)'
            }} />
            <div style={{
              marginBottom: '20px',
              display: 'flex',
              justifyContent: 'flex-start'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '16px',
                background: 'rgba(56, 161, 105, 0.1)'
              }}>
                <img
                  src="/FindTourn_Icon.png"
                  alt="For Players"
                  loading="lazy"
                  style={{
                    width: '40px',
                    height: '40px',
                    objectFit: 'contain'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
            <h3 style={{
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 0 24px 0',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-display)'
            }}>
              For Players
            </h3>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0
            }}>
              {[
                'Find tournaments near you with map search',
                'Filter by day of week, format, and distance',
                'Get venue details and directions instantly',
                'View live brackets and standings in real-time',
                'No account needed to browse and discover'
              ].map((item, index) => (
                <li
                  key={index}
                  style={{
                    padding: '14px 0',
                    borderBottom: index < 4 ? '1px solid var(--border-light)' : 'none',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '14px',
                    fontSize: '15px',
                    color: 'var(--text-primary)'
                  }}
                >
                  <span style={{
                    color: 'var(--success)',
                    fontWeight: 'bold',
                    fontSize: '18px',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    background: 'rgba(56, 161, 105, 0.1)',
                    flexShrink: 0
                  }}>✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* For Hosts */}
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '20px',
            padding: '40px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
            border: '1px solid var(--border-light)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Accent gradient border */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '4px',
              height: '100%',
              background: 'linear-gradient(180deg, #667eea, #764ba2)'
            }} />
            <div style={{
              marginBottom: '20px',
              display: 'flex',
              justifyContent: 'flex-start'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '16px',
                background: 'rgba(102, 126, 234, 0.1)'
              }}>
                <img
                  src="/Host_Icon.png"
                  alt="For Hosts"
                  loading="lazy"
                  style={{
                    width: '40px',
                    height: '40px',
                    objectFit: 'contain'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
            <h3 style={{
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 0 24px 0',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-display)'
            }}>
              For Hosts
            </h3>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0
            }}>
              {[
                'Set up tournaments in under 5 minutes',
                'Automatic table assignment saves hours',
                'Share with a simple link - no app required',
                'Schedule recurring weekly/monthly events',
                'Custom branding with logos and sponsors'
              ].map((item, index) => (
                <li
                  key={index}
                  style={{
                    padding: '14px 0',
                    borderBottom: index < 4 ? '1px solid var(--border-light)' : 'none',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '14px',
                    fontSize: '15px',
                    color: 'var(--text-primary)'
                  }}
                >
                  <span style={{
                    color: 'var(--accent-primary)',
                    fontWeight: 'bold',
                    fontSize: '18px',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    background: 'rgba(102, 126, 234, 0.1)',
                    flexShrink: 0
                  }}>✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section style={{
        padding: '100px 20px',
        background: 'linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-secondary) 100%)',
        color: 'var(--text-on-dark)',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(circle at 10% 90%, rgba(255,255,255,0.1) 0%, transparent 40%),
            radial-gradient(circle at 90% 10%, rgba(255,255,255,0.1) 0%, transparent 40%)
          `,
          pointerEvents: 'none'
        }} />

        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 1
        }}>
          <h2 style={{
            fontSize: 'clamp(32px, 5vw, 48px)',
            fontWeight: '800',
            margin: '0 0 20px 0',
            fontFamily: 'var(--font-display)',
            letterSpacing: '-0.02em'
          }}>
            Ready to Get Started?
          </h2>
          <p style={{
            fontSize: 'clamp(16px, 3vw, 20px)',
            margin: '0 0 48px 0',
            opacity: 0.95,
            lineHeight: '1.6'
          }}>
            No credit card required. No downloads. Start managing tournaments in seconds.
          </p>
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => onNavigate('local')}
              style={{
                padding: '18px 36px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--accent-primary)',
                border: 'none',
                borderRadius: '12px',
                fontSize: '17px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                fontFamily: 'var(--font-display)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-3px)';
                e.target.style.boxShadow = '0 12px 32px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.15)';
              }}
            >
              Start Free Tournament
            </button>
            <button
              onClick={() => onNavigate('finder')}
              style={{
                padding: '18px 36px',
                backgroundColor: 'transparent',
                color: 'var(--text-on-dark)',
                border: '2px solid rgba(255, 255, 255, 0.5)',
                borderRadius: '12px',
                fontSize: '17px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontFamily: 'var(--font-display)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--bg-secondary)';
                e.target.style.color = 'var(--accent-primary)';
                e.target.style.borderColor = 'var(--bg-secondary)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = 'var(--text-on-dark)';
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
              }}
            >
              Browse Tournaments
            </button>
          </div>
          {!isAuthenticated && (
            <div style={{
              marginTop: '36px',
              fontSize: '16px',
              opacity: 0.9
            }}>
              Or{' '}
              <span
                onClick={() => onShowAuth('signup')}
                style={{
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                create an account
              </span>
              {' '}to save and share your tournaments
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: '48px 20px',
        backgroundColor: 'var(--bg-footer)',
        color: 'var(--text-on-dark)',
        textAlign: 'center'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <p style={{
            margin: '0 0 20px 0',
            fontSize: '15px',
            opacity: 0.7
          }}>
            © {new Date().getFullYear()} Pool Tournaments. Created by Ian Waters.
          </p>
          <div style={{
            display: 'flex',
            gap: '24px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            fontSize: '14px'
          }}>
            <a
              href="/contact"
              onClick={(e) => { e.preventDefault(); onNavigate('contact'); }}
              style={{
                color: 'var(--text-on-dark)',
                cursor: 'pointer',
                textDecoration: 'none',
                opacity: 0.7,
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.opacity = 1}
              onMouseLeave={(e) => e.target.style.opacity = 0.7}
            >
              Contact
            </a>
            <a
              href="/privacy"
              onClick={(e) => { e.preventDefault(); onNavigate('privacy'); }}
              style={{
                color: 'var(--text-on-dark)',
                cursor: 'pointer',
                textDecoration: 'none',
                opacity: 0.7,
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.opacity = 1}
              onMouseLeave={(e) => e.target.style.opacity = 0.7}
            >
              Privacy
            </a>
            <a
              href="/terms"
              onClick={(e) => { e.preventDefault(); onNavigate('terms'); }}
              style={{
                color: 'var(--text-on-dark)',
                cursor: 'pointer',
                textDecoration: 'none',
                opacity: 0.7,
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.opacity = 1}
              onMouseLeave={(e) => e.target.style.opacity = 0.7}
            >
              Terms
            </a>
            <a
              href="/help"
              onClick={(e) => { e.preventDefault(); onNavigate('help'); }}
              style={{
                color: 'var(--text-on-dark)',
                cursor: 'pointer',
                textDecoration: 'none',
                opacity: 0.7,
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.opacity = 1}
              onMouseLeave={(e) => e.target.style.opacity = 0.7}
            >
              Help
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
